import { lazy } from 'react';

/**
 * React.lazy that survives a failed chunk request.
 *
 * Route chunks are content-hashed, so every deploy retires the URLs the
 * currently-open tabs know about (server.js 404s anything under /static/ that
 * is not on disk). A tab left open across a deploy — or a single dropped
 * request on a flaky network — makes the dynamic import reject, and React
 * hands that rejection to the nearest error boundary: the visitor gets an
 * error page instead of the route.
 *
 * Recovery is two-staged: retry the import once for a transient blip, then
 * reload the page so the browser picks up the current asset manifest. The
 * reload happens at most once per session (a stale bundle cannot survive a
 * reload, so a second failure is a real one) and only for chunk-load errors —
 * everything else propagates untouched.
 */

const RELOAD_KEY = 'membrowse:chunk-reloaded';
const RETRY_DELAY_MS = 350;

// The guard has to survive the very reload it protects against, so it is
// written to two places and read from either. sessionStorage is the primary
// store but throws outright where storage is blocked (cookie-blocking settings,
// sandboxed iframes); the current history entry is the fallback, since the
// browser hands its state back intact after a reload. Losing both would mean an
// unreachable chunk reloads the page on every load, forever.
const readStorageFlag = () => {
  try {
    return window.sessionStorage.getItem(RELOAD_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeStorageFlag = (value) => {
  try {
    if (value) {
      window.sessionStorage.setItem(RELOAD_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(RELOAD_KEY);
    }
  } catch {
    // Storage unavailable — the history entry carries the guard instead.
  }
};

const readHistoryFlag = () => {
  try {
    return window.history.state?.[RELOAD_KEY] === true;
  } catch {
    return false;
  }
};

const writeHistoryFlag = (value) => {
  try {
    // Merge rather than replace: react-router keeps its own bookkeeping
    // (usr/key/idx) in this same state object.
    const state = { ...(window.history.state || {}) };
    if (value) {
      state[RELOAD_KEY] = true;
    } else {
      delete state[RELOAD_KEY];
    }
    window.history.replaceState(state, '');
  } catch {
    // Nothing left to fall back to; the browser's reload throttling is all
    // that remains.
  }
};

const hasReloaded = () => readStorageFlag() || readHistoryFlag();

const setReloadFlag = () => {
  writeStorageFlag(true);
  writeHistoryFlag(true);
};

const clearReloadFlag = () => {
  writeStorageFlag(false);
  // Only touch history when a marker is actually there, so a healthy load does
  // not call replaceState for every chunk it fetches.
  if (readHistoryFlag()) {
    writeHistoryFlag(false);
  }
};

/**
 * True for the webpack/browser errors raised when a chunk cannot be fetched or
 * parsed. Matched by shape rather than by class: the name is set by webpack,
 * the message wording differs across browsers for a failed module script.
 */
export const isChunkLoadError = (error) => {
  if (!error) {
    return false;
  }
  if (error.name === 'ChunkLoadError') {
    return true;
  }
  const message = String(error.message || '');
  return /Loading chunk .* failed/i.test(message)
    || /Loading CSS chunk .* failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /error loading dynamically imported module/i.test(message);
};

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * @param {() => Promise<{default: React.ComponentType}>} factory - the same
 *   `() => import('./Thing')` you would pass to React.lazy.
 * @returns {React.LazyExoticComponent}
 */
const loaded = (module) => {
  clearReloadFlag();
  return module;
};

export const lazyWithRetry = (factory) => lazy(() => factory()
  .then(loaded)
  .catch((error) => {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    // webpack clears the chunk's install record on failure, so calling the
    // factory again issues a fresh request rather than replaying the rejection.
    return delay(RETRY_DELAY_MS).then(factory).then(loaded).catch((retryError) => {
      // The second attempt can fail for a different reason — the chunk may
      // arrive and then blow up while evaluating. Reloading would throw away
      // the visitor's state without fixing anything, so only a genuine
      // fetch failure earns one.
      if (!isChunkLoadError(retryError) || hasReloaded()) {
        throw retryError;
      }
      setReloadFlag();
      window.location.reload();
      // Keep React suspended while the document tears down, so the error
      // boundary never flashes on top of a page that is already reloading.
      return new Promise(() => {});
    });
  }));

export default lazyWithRetry;
